import { useState, useEffect, useRef, useCallback } from 'react';
import { Globe, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LANGUAGES = [
  { code: 'en', name: 'English', native: 'English' },
  { code: 'hi', name: 'Hindi', native: 'हिन्दी' },
  { code: 'mr', name: 'Marathi', native: 'मराठी' },
  { code: 'ta', name: 'Tamil', native: 'தமிழ்' },
  { code: 'te', name: 'Telugu', native: 'తెలుగు' },
  { code: 'kn', name: 'Kannada', native: 'ಕನ್ನಡ' },
  { code: 'ml', name: 'Malayalam', native: 'മലയാളം' },
  { code: 'bn', name: 'Bengali', native: 'বাংলা' },
  { code: 'gu', name: 'Gujarati', native: 'ગુજરાતી' },
  { code: 'pa', name: 'Punjabi', native: 'ਪੰਜਾਬੀ' },
  { code: 'or', name: 'Odia', native: 'ଓଡ଼ିଆ' },
  { code: 'as', name: 'Assamese', native: 'অসমীয়া' },
  { code: 'ur', name: 'Urdu', native: 'اردو' },
];

// Store original texts for reverting
const originalTexts = new Map();

const LanguageSelector = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState(() => localStorage.getItem('app_lang') || 'en');
  const [translating, setTranslating] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-translate on mount if language was previously set
  useEffect(() => {
    const savedLang = localStorage.getItem('app_lang');
    if (savedLang && savedLang !== 'en') {
      // Translate after initial render and after a delay for dynamic content
      const timer1 = setTimeout(() => translatePage(savedLang), 500);
      const timer2 = setTimeout(() => translatePage(savedLang), 2000);
      return () => { clearTimeout(timer1); clearTimeout(timer2); };
    }
  }, []);

  const getTranslatableElements = () => {
    const selectors = [
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'p', 'span', 'label', 'button', 'a',
      'th', 'td', 'li', 'dt', 'dd', 'div',
      '[placeholder]',
    ];
    const elements = document.querySelectorAll(selectors.join(','));
    const result = [];

    elements.forEach(el => {
      // Skip the language selector itself
      if (el.closest('[data-testid="language-selector"]')) return;
      // Skip script/style/svg
      const tag = el.tagName;
      if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'SVG' || tag === 'PATH') return;
      // Skip hidden elements
      if (el.offsetParent === null && tag !== 'INPUT') return;
      // Collect direct text node(s) — handles fragmented text nodes too
      if (tag !== 'INPUT' && tag !== 'TEXTAREA') {
        const directTextNodes = Array.from(el.childNodes).filter(
          n => n.nodeType === Node.TEXT_NODE && n.textContent.trim().length > 0
        );
        directTextNodes.forEach(node => {
          const txt = node.textContent.trim();
          if (txt.length > 1) {
            result.push({ el, node, type: 'textnode', text: txt });
          }
        });
      }
      // Handle placeholders
      if (el.hasAttribute && el.hasAttribute('placeholder')) {
        result.push({ el, type: 'placeholder', text: el.getAttribute('placeholder') });
      }
    });

    return result;
  };

  const translatePage = useCallback(async (langCode) => {
    if (langCode === 'en') {
      // Revert to original texts
      originalTexts.forEach((original, key) => {
        const [type, ...rest] = key.split('::');
        // Can't reliably revert all, so reload
      });
      localStorage.setItem('app_lang', 'en');
      setCurrentLang('en');
      window.location.reload();
      return;
    }

    setTranslating(true);
    try {
      const elements = getTranslatableElements();
      const textsToTranslate = [];
      const textSet = new Set();

      elements.forEach(({ text }) => {
        const cleaned = text.trim();
        if (cleaned.length > 1 && !textSet.has(cleaned) && /[a-zA-Z]/.test(cleaned)) {
          textSet.add(cleaned);
          textsToTranslate.push(cleaned);
        }
      });

      if (textsToTranslate.length === 0) {
        setTranslating(false);
        return;
      }

      // Batch in chunks of 50
      const chunkSize = 50;
      const allTranslations = {};

      for (let i = 0; i < textsToTranslate.length; i += chunkSize) {
        const chunk = textsToTranslate.slice(i, i + chunkSize);
        try {
          const res = await fetch(`${BACKEND_URL}/api/translate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ texts: chunk, target_lang: langCode, source_lang: 'en' }),
          });
          if (res.ok) {
            const data = await res.json();
            Object.assign(allTranslations, data.translations);
          }
        } catch (err) {
          console.error('Translation chunk failed:', err);
        }
      }

      // Apply translations
      elements.forEach((entry) => {
        const { el, type, text } = entry;
        const cleaned = text.trim();
        const translated = allTranslations[cleaned];
        if (translated && translated !== cleaned) {
          if (type === 'textnode') {
            // Replace the specific text node directly
            const node = entry.node;
            if (node && node.nodeType === Node.TEXT_NODE) {
              if (!originalTexts.has(node)) {
                originalTexts.set(node, node.textContent);
              }
              // Preserve surrounding whitespace
              const leading = node.textContent.match(/^\s*/)[0];
              const trailing = node.textContent.match(/\s*$/)[0];
              node.textContent = leading + translated + trailing;
            }
          } else if (type === 'placeholder') {
            if (!el.dataset.origPlaceholder) {
              el.dataset.origPlaceholder = el.getAttribute('placeholder');
            }
            el.setAttribute('placeholder', translated);
          }
        }
      });

      localStorage.setItem('app_lang', langCode);
    } catch (error) {
      console.error('Translation failed:', error);
    }
    setTranslating(false);
  }, []);

  const changeLanguage = (langCode) => {
    setCurrentLang(langCode);
    setIsOpen(false);
    localStorage.setItem('app_lang', langCode);
    translatePage(langCode);
  };

  const selectedLang = LANGUAGES.find(l => l.code === currentLang) || LANGUAGES[0];

  return (
    <div className="relative" ref={dropdownRef} data-testid="language-selector">
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={translating}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-background/80 backdrop-blur border border-border/50 hover:bg-muted/80 transition-colors text-sm"
        data-testid="language-toggle"
      >
        {translating ? (
          <Loader2 className="h-4 w-4 text-primary animate-spin" />
        ) : (
          <Globe className="h-4 w-4 text-primary" />
        )}
        <span className="font-medium">{selectedLang.native}</span>
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-56 bg-background border border-border rounded-lg shadow-xl z-[9999] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="max-h-72 overflow-y-auto">
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                className={`w-full text-left px-4 py-2.5 text-sm hover:bg-muted/50 transition-colors flex items-center justify-between ${
                  currentLang === lang.code ? 'bg-primary/10 text-primary font-medium' : ''
                }`}
                data-testid={`lang-${lang.code}`}
              >
                <div>
                  <span>{lang.native}</span>
                  <span className="text-xs text-muted-foreground ml-2">({lang.name})</span>
                </div>
                {currentLang === lang.code && (
                  <div className="h-2 w-2 rounded-full bg-primary" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
